import React from 'react';

/**
 * Loading spinner props interface
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  fullPage?: boolean;
  inline?: boolean;
  className?: string;
}

/**
 * Standardized loading spinner component
 * Provides consistent loading states across the application
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  text = 'Loading...',
  fullPage = false,
  inline = false,
  className = ''
}) => {
  // Determine spinner size classes
  const getSpinnerClasses = () => {
    let classes = 'spinner-border';
    
    switch (size) {
      case 'sm':
        classes += ' spinner-border-sm';
        break;
      case 'lg':
        classes += ' spinner-border-lg';
        break;
      default:
        // md is default, no additional class needed
        break;
    }
    
    return classes;
  };

  // Determine container classes
  const getContainerClasses = () => {
    let classes = '';
    
    if (fullPage) {
      classes = 'container text-center mt-5';
    } else if (inline) {
      classes = 'd-inline-flex align-items-center';
    } else {
      classes = 'text-center py-3';
    }
    
    return classes + (className ? ` ${className}` : '');
  };

  // Render the spinner
  const renderSpinner = () => (
    <div className={getSpinnerClasses()} role="status">
      <span className="visually-hidden">{text}</span>
    </div>
  );

  // Render text if not inline
  const renderText = () => {
    if (inline) return null;
    
    return (
      <p className={`mt-2 mb-0 ${size === 'sm' ? 'small' : ''}`}>
        {text}
      </p>
    );
  };

  // For inline usage, just return the spinner with text in the same line
  if (inline) {
    return (
      <span className={getContainerClasses()}>
        {renderSpinner()}
        {text && <span className="ms-2">{text}</span>}
      </span>
    );
  }

  // For full page or block usage
  return (
    <div className={getContainerClasses()}>
      {renderSpinner()}
      {renderText()}
    </div>
  );
};

export default LoadingSpinner; 